<template>
  <div v-if="isLoading" class="menu-fullscreen-loading">
    <loading>Loading...</loading>
  </div>
  <div v-else class="band-view">
    <div class="band-view-left">
      <section class="section settings">
        <legend>Settings</legend>
        <div class="section-sub">
          <label>Name</label>
          <input class="input" type="text" v-model="name" />
        </div>
        <div class="section-sub buttons">
          <button @click.prevent.stop="saveSettings" class="btn-outline">
            Save Settings
          </button>
        </div>
      </section>
      <section class="section danger">
        <legend>Danger</legend>
        <div class="section-sub buttons">
          <button
            @click.prevent.stop="deleteConfirm = true"
            class="btn-outline btn-danger"
          >
            Destroy Band
          </button>
        </div>
      </section>
    </div>
    <div class="band-view-right">
      <section class="section members">
        <legend>Band Members</legend>
        <div class="section-sub">
          <div class="band-submitted-by">
            Submitted by
            <b>{{ band.owner.first_name }} {{ band.owner.last_name }}</b>
          </div>
          <div class="small">
            The band owner is implied as a band member so can't be added to the
            member list
          </div>
        </div>
        <div class="section-sub">
          <label>Search Users</label>
          <users-autocomplete @pick="addMember" />
        </div>
        <div class="section-sub member-list">
          <div v-for="user in members" :key="user._id" class="band-member">
            <x-mark-icon @click.prevent.stop="removeMember(user)" />
            <span>{{ user.first_name }} {{ user.last_name }}</span>
          </div>
        </div>
        <div class="section-sub buttons">
          <button @click.prevent.stop="saveMembers" class="btn-outline">
            Update Members
          </button>
        </div>
      </section>
    </div>
  </div>
  <confirm
    title="Destroy Band"
    v-if="deleteConfirm"
    @ok="destroyBand"
    @click="deleteConfirm = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { mapActions, mapMutations, mapState } from "vuex";
import { Band, User } from "@/models";
import Loading from "@/components/Loading.vue";
import Confirm from "@/components/Confirm.vue";
import UsersAutocomplete from "@/components/Autocomplete/UsersAutocomplete.vue";
import { XMarkIcon } from "@heroicons/vue/24/solid";
@Options({
  components: {
    Confirm,
    Loading,
    UsersAutocomplete,
    XMarkIcon,
  },
  data() {
    return {
      isLoading: true,
      deleteConfirm: false,
      name: "",
    };
  },
  methods: {
    ...mapMutations("bands", ["addMember", "removeMember"]),
    ...mapActions("bands", ["saveMembers"]),
  },
  computed: {
    ...mapState("bands", {
      band: "current",
    }),
  },
  watch: {
    "$route.params"() {
      if (this.$route.params.id) {
        this.reload();
      }
    },
  },
})
export default class BandView extends Vue {
  isLoading!: boolean;
  band!: Band;
  deleteConfirm!: boolean;

  name!: string;

  addMember!: (user: User) => void;
  removeMember!: (user: User) => void;
  saveMembers!: () => Promise<void>;

  reload() {
    this.isLoading = true;
    this.$store
      .dispatch("bands/loadCurrent", this.$route.params.id)
      .then(() => {
        this.isLoading = false;
        this.pullBandFields();
        document.title = `${this.band.name} - Eavesdrop`;
      });
  }

  mounted() {
    this.reload();
  }

  destroyBand() {
    console.log("destroy band");
    this.$store.dispatch("bands/destroyBand", this.band._id).then(() => {
      this.$router.replace("/projects");
    });
  }

  pullBandFields() {
    this.name = this.band.name;
  }

  saveSettings() {
    this.$store.dispatch("bands/updateBand", { name: this.name }).then(() => {
      this.pullBandFields();
    });
  }

  get members() {
    return this.band.members.filter((user) => user._id !== this.band.owner._id);
  }
}
</script>

<style lang="scss">
.band-view {
  background: #d0d0d0;
  min-height: calc(100vh - 24px);
  border-top: 1px solid #999;
  box-sizing: border-box;
  display: flex;
  padding: 12px;

  .band-view-left {
    width: 350px;
    margin-right: 1em;
  }
  .band-view-right {
    flex-grow: 1;
  }

  .band-member {
    margin-bottom: 2px;
    svg {
      width: 12px;
      position: relative;
      top: 1.5px;
      cursor: pointer;
    }
    svg:hover ~ span {
      text-decoration: line-through;
    }
  }

  .section.members {
    min-height: 200px;
    display: flex;
    flex-direction: column;

    .section-sub.member-list {
      flex-grow: 1;
    }
  }

  .users-autocomplete {
    width: 350px;
  }
}
</style>
